import { useCallback, useMemo } from "react";
import moment from "moment";
import { ListMessageRequest } from "../services/soap/message/requests/ListMessageRequest";
import { GetListMessageRequest } from "../services/soap/message/requests/GetListMessageRequest";
import { GetMessageRequest } from "../services/soap/message/requests/GetMessageRequest";
import { SetSeenMessageRequest } from "../services/soap/message/requests/SetSeenMessageRequest";
import { DeleteMessageRequest } from "../services/soap/message/requests/DeleteMessageRequest";
import { GetContactRequest } from "../services/soap/main/requests/GetContactRequest";
import { ListMessageTemplateRequest } from "../services/soap/message/requests/ListMessageTemplateRequest";
import { GetMessageTemplateRequest } from "../services/soap/message/requests/GetMessageTemplateRequest";
import type { RefObject } from "react";

// Minimal refs required for message operations
interface MessageOperationsRefs {
  filterParamRef: RefObject<{
    createdFrom: string;
    createdTo: string;
    sender: string;
    subject: string;
    newOnly: boolean;
    starred: boolean;
  }>;
  offSetLimitRef: RefObject<number>;
  searchedTextRef?: RefObject<string>;
}

interface UseMessageOperationsProps {
  store?: any;
  messageService?: any;
  mainService?: any;
  messageType?: string;
  refs: MessageOperationsRefs;
  messageNodeId?: number;
  /** For MessageListComponent - uses treeHandle with fromNode mode */
  treeHandle?: string | number;
}

export function useMessageOperations({
  store,
  messageService,
  mainService,
  messageType,
  refs,
  messageNodeId,
  treeHandle,
}: UseMessageOperationsProps) {
  const PAGE_SIZE = 50;

  // Determine if using treeHandle mode (MessageListComponent) or messageNodeId mode (MessagesComponent)
  const isTreeHandleMode = treeHandle !== undefined;

  // Memoized filter parameter updater
  const updateFilterParam = useCallback(() => {
    if (refs.filterParamRef.current.createdTo) {
      refs.filterParamRef.current.createdTo =
        moment(refs.filterParamRef.current.createdTo)
          .add(1, "days")
          .format("YYYY-MM-DD") + "T00:00:00";
    }
    if (refs.filterParamRef.current.createdFrom) {
      refs.filterParamRef.current.createdFrom =
        moment(refs.filterParamRef.current.createdFrom).format("YYYY-MM-DD") +
        "T00:00:00";
    }
  }, [refs.filterParamRef]);

  // Optimized message list count fetcher
  const getMessageListCount = useCallback(
    async (filterText: string, signal?: AbortSignal): Promise<number> => {
      try {
        if (signal?.aborted) {
          throw new Error("Request aborted");
        }
        if (messageType !== "concept") {
          const messageRes = await messageService.listMessage(
            new ListMessageRequest(
              store.Server,
              store.SessionId,
              createReqParam(true, filterText),
            ),
          );

          if (messageRes?.result === "OK") {
            return messageRes.count || 0;
          }
        } else {
          const messageRes = await messageService.listMessageTemplate(
            new ListMessageTemplateRequest(store.Server, store.SessionId, {
              offset: 0,
              limit: 30,
              treeHandle: messageNodeId,
              draftsOnly: true,
              onlyDefault: 0,
              language: "",
              ORDER: {
                COL: [
                  {
                    name: "created",
                    desc: 1,
                  },
                ],
              },
            }),
          );
          if (messageRes?.result === "OK") {
            return messageRes.count || 0;
          }
        }
        return 0;
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log("Message count request aborted");
          return 0;
        }
        console.error("Error fetching message count:", error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store, messageService, messageType, messageNodeId],
  );

  const createReqParam = useCallback(
    (isTotalCount: boolean, filterText: any) => {
      // TreeHandle mode (for MessageListComponent) - uses fromNode: true with treeHandle
      if (isTreeHandleMode) {
        const treeHandleParams: any = {
          offset: refs.offSetLimitRef.current,
          limit: PAGE_SIZE,
          dateTo: refs.filterParamRef.current.createdTo,
          dateFrom: refs.filterParamRef.current.createdFrom,
          contains: filterText,
          senderPlain: refs.filterParamRef.current.sender,
          newOnly: refs.filterParamRef.current.newOnly,
          starred: refs.filterParamRef.current.starred,
          subjectContains: refs.filterParamRef.current.subject,
          countOnly: isTotalCount,
          order: "down",
          orderBy: "created",
          fromNode: true,
          treeHandle: treeHandle,
          showInbox: false,
          tree: 0,
          showDeleted: false,
          showSent: false,
          showDrafts: false,
          showAll: false,
          showAllMy: false,
        };

        if (isTotalCount) {
          delete treeHandleParams.offset;
          delete treeHandleParams.limit;
          delete treeHandleParams.order;
        }

        return treeHandleParams;
      }

      // Standard mode (for MessagesComponent)
      const baseParams: any = {
        offset: refs.offSetLimitRef.current,
        limit: PAGE_SIZE,
        dateTo: refs.filterParamRef.current.createdTo,
        dateFrom: refs.filterParamRef.current.createdFrom,
        contains: filterText,
        senderPlain: refs.filterParamRef.current.sender,
        newOnly: refs.filterParamRef.current.newOnly,
        starred: refs.filterParamRef.current.starred,
        subjectContains: refs.filterParamRef.current.subject,
        countOnly: isTotalCount,
        order: "down",
        orderBy: "created",
        fromNode: false,
        tree: 0,
        showDeleted: false,
        showSent: false,
        showDrafts: false,
        showAll: true,
        showAllMy: false,
      };

      if (isTotalCount) {
        delete baseParams.offset;
        delete baseParams.limit;
      }

      const typeConfigs: Record<string, any> = {
        todo: { showInbox: true },
        messages: { showInbox: false, showAll: false, showAllMy: true },
        sent: {
          newOnly: false,
          showInbox: false,
          showSent: true,
          starred: false,
        },
        trash: { showInbox: false, showDeleted: true },
        corporate: { showInbox: false },
        concept: { showInbox: false, showDrafts: true },
      };

      return {
        ...baseParams,
        ...((messageType && typeConfigs[messageType]) || {}),
      };
    },
    [
      messageType,
      refs.offSetLimitRef,
      refs.filterParamRef,
      isTreeHandleMode,
      treeHandle,
    ],
  );

  // Optimized message loader with error handling
  const loadMessages = useCallback(
    async (filterText: string, signal?: AbortSignal): Promise<any[]> => {
      try {
        if (signal?.aborted) {
          throw new Error("Request aborted");
        }
        updateFilterParam();
        let messageRes = [];
        if (messageType !== "concept") {
          messageRes = await messageService.listMessage(
            new ListMessageRequest(
              store.Server,
              store.SessionId,
              createReqParam(false, filterText),
            ),
          );
        } else {
          messageRes = await messageService.listMessageTemplate(
            new ListMessageTemplateRequest(store.Server, store.SessionId, {
              offset: 0,
              limit: 30,
              treeHandle: messageNodeId,
              draftsOnly: true,
              onlyDefault: 0,
              language: "",
              ORDER: {
                COL: [
                  {
                    name: "created",
                    desc: 1,
                  },
                ],
              },
            }),
          );
        }

        const handleMessageResponse = async (
          messageListRes: any,
        ): Promise<any[]> => {
          if (messageListRes?.result === "OK" && messageListRes?.count > 0) {
            return messageListRes.MESSAGE || [];
          }
          return [];
        };

        if (filterText !== "" && messageRes?.result === "OK") {
          const messageListRes = await messageService.getListMessage(
            new GetListMessageRequest(
              store.Server,
              store.SessionId,
              messageRes.listId,
            ),
          );
          return messageType === "concept"
            ? messageListRes.ITEM || []
            : handleMessageResponse(messageListRes);
        }

        return messageType === "concept"
          ? messageRes.ITEM || []
          : handleMessageResponse(messageRes);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log("Message load request aborted");
          return [];
        }
        console.error("Error loading messages:", error);
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      store,
      messageService,
      messageType,
      updateFilterParam,
      messageNodeId,
      createReqParam,
    ],
  );

  // Optimized participant name fetcher
  const fetchParticipantsNames = useCallback(
    async (contactRelations: any[]): Promise<string> => {
      if (!contactRelations || contactRelations.length === 0) {
        return "";
      }

      const formatContactName = (res: any): string => {
        if (res.PERSON) {
          const prefix = res.PERSON?.name3 || "";
          return `${prefix} ${res.PERSON?.name1}`.trim();
        } else if (res.COMPANY) {
          const prefix = res.COMPANY?.name3 || "";
          return `${prefix} ${res.COMPANY?.name1}`.trim();
        }
        return "";
      };

      try {
        const participantPromises = contactRelations.map((item: any) =>
          mainService.getContact(
            new GetContactRequest(
              store.Server,
              store.SessionId,
              undefined,
              undefined,
              item.contact,
            ),
          ),
        );

        const responses = await Promise.all(participantPromises);
        const participantsNames = responses
          .filter((res: any) => res?.result === "OK")
          .map(formatContactName)
          .filter((name) => name.length > 0);

        return participantsNames.join(", ");
      } catch (error) {
        console.error("Error fetching participant names:", error);
        return "";
      }
    },
    [store, mainService],
  );

  // Optimized email content fetcher
  const getEmailContent = useCallback(
    async (selectedRow: any): Promise<any> => {
      try {
        let messageContent;
        if (messageType !== "concept") {
          messageContent = await messageService.getMessage(
            new GetMessageRequest(
              store.Server,
              store.SessionId,
              selectedRow.id,
            ),
          );

          // Mark as seen if unread
          if (selectedRow.seen === "0") {
            await messageService.setSeenMessage(
              new SetSeenMessageRequest(
                store.Server,
                store.SessionId,
                selectedRow.id,
              ),
            );
          }

          // Fetch participants if available
          if (
            messageContent.MESSAGE?.CONTACT_RELATIONS?.CONTACT_RELATION
              ?.length > 0
          ) {
            const participantsName = await fetchParticipantsNames(
              messageContent.MESSAGE.CONTACT_RELATIONS.CONTACT_RELATION.filter(
                (item: any) => item.type === "0",
              ),
            );
            messageContent.participantsName = participantsName;
          }
        } else {
          messageContent = await messageService.getMessageTemplate(
            new GetMessageTemplateRequest(
              store.Server,
              store.SessionId,
              selectedRow.id,
            ),
          );
        }

        return messageContent;
      } catch (error) {
        console.error("Error fetching email content:", error);
        throw error;
      }
    },
    [store, messageService, fetchParticipantsNames, messageType],
  );

  // Optimized message deletion
  const deleteMessage = useCallback(
    async (messageId: string): Promise<boolean> => {
      try {
        const delMessageRes = await messageService.deleteMessage(
          new DeleteMessageRequest(store.Server, store.SessionId, {
            realDelete: false,
            MESSAGE: {
              id: messageId,
              parent: messageId,
            },
          }),
        );

        return delMessageRes?.result === "OK";
      } catch (error) {
        console.error("Error deleting message:", error);
        return false;
      }
    },
    [store, messageService],
  );

  // Utility functions
  const utils = useMemo(
    () => ({
      getSenderName: (fullName: string): string => {
        const regex = /(.*)<.*>/;
        const match = regex.exec(fullName);
        return match ? match[1].trim() : fullName;
      },

      getSenderEmail: (email: string): string => {
        const match = /<(.+?)>/.exec(email);
        return match ? match[1] : email;
      },

      oldEmailContentFix: (emailContent: string): string => {
        const regex = /padding:\s*16px;/g;
        //if regex is simple text without any html wrap it with pre tags
        const isSimpleText = !/<[^>]+>/.test(emailContent);
        if (isSimpleText) {
          return `<pre>${emailContent}</pre>`;
        }

        return emailContent?.replace(regex, "") || "";
      },
    }),
    [],
  );

  return {
    getMessageListCount,
    loadMessages,
    fetchParticipantsNames,
    getEmailContent,
    deleteMessage,
    updateFilterParam,
    utils,
    PAGE_SIZE,
  };
}
